import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface SimulationState {
  is_enabled: boolean;
  starting_balance: number;
  virtual_balance: number;
  last_reset_at?: string | null;
}

interface SimulationContextValue {
  state: SimulationState | null;
  isLoading: boolean;
  error: string | null;
  refreshState: () => Promise<void>;
  toggleSimulation: (enabled: boolean, startingBalance?: number) => Promise<void>;
  resetSimulation: (startingBalance?: number) => Promise<void>;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const getAuthHeader = (): string | null => {
  const tokensJson = localStorage.getItem('authTokens');
  if (tokensJson) {
    try {
      const tokens = JSON.parse(tokensJson);
      return tokens.access_token ? `Bearer ${tokens.access_token}` : null;
    } catch { /* ignore */ }
  }
  return null;
};

const SimulationContext = createContext<SimulationContextValue>({
  state: null,
  isLoading: false,
  error: null,
  refreshState: async () => {},
  toggleSimulation: async () => {},
  resetSimulation: async () => {},
});

export const useSimulation = () => useContext(SimulationContext);

export const SimulationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<SimulationState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const makeRequest = useCallback(async <T,>(endpoint: string, options: RequestInit = {}): Promise<T> => {
    const auth = getAuthHeader();
    if (!auth) throw new Error('Nicht authentifiziert');

    const response = await fetch(`${API_BASE_URL}/simulation${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: auth,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }, []);

  const refreshState = useCallback(async () => {
    const auth = getAuthHeader();
    if (!auth) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await makeRequest<SimulationState>('/state');
      setState(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [makeRequest]);

  const toggleSimulation = useCallback(async (enabled: boolean, startingBalance?: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await makeRequest<SimulationState>('/toggle', {
        method: 'POST',
        body: JSON.stringify({
          is_enabled: enabled,
          reset_on_enable: true,
          starting_balance: startingBalance,
        }),
      });
      setState(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [makeRequest]);

  const resetSimulation = useCallback(async (startingBalance?: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await makeRequest<SimulationState>('/reset', {
        method: 'POST',
        body: JSON.stringify({ starting_balance: startingBalance }),
      });
      setState(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [makeRequest]);

  useEffect(() => {
    refreshState();
  }, [refreshState]);

  return (
    <SimulationContext.Provider value={{ state, isLoading, error, refreshState, toggleSimulation, resetSimulation }}>
      {children}
    </SimulationContext.Provider>
  );
};

export default SimulationContext;
